import { useState } from 'react';
import type {
  Apis,
  Providers,
  Reports,
  Utils,
} from '@sparrow-marketing/contracts';
import { createContainer } from 'unstated-next';

interface IUser {
  avatar: string;
  name: string;
}
export interface IReport extends Utils.ApiModel<Apis.SparrowApi.IReport<any>> {
  pageTypes: Providers.Accounts.ServiceTypes[];
  schedule?: Reports.ReportSchedule.IReportSchedule;
  // TODO: change it once we add password protect
  passwordProtected: boolean;
  user: IUser;
  previewImage: string;
}

export type ReportTemplate = Utils.Model<Apis.SparrowApi.IReportTemplate<any>>;

interface IState {
  templates: ReportTemplate[];
  reports: IReport[];
  total: number;
  pagination: {
    pageNum: number;
  };
  isFirstLoad: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  myTemplates: ReportTemplate[];
}

export interface ReportsHook {
  state: IState;
  updateReportById: (id: string, updatedReport: IReport) => void;
  updateReports: (reports: IReport[]) => void;
  updatePendingReportToFail: (pendingReportIds: number[]) => void;
  appendReports: (reports: IReport[], skipIndex: number) => void;
  setPageNum: (pageNum: number) => void;
  updateTotal: (total: number) => void;
  resetReportsState: () => void;
  updateFirstLoad: (isFirstLoad: boolean) => void;
  updateIsLoaded: (isFirstLoad: boolean) => void;
  resetReports: () => void;
  updateIsLoading: (isLoading: boolean) => void;
}

const initialState: IState = {
  reports: [],
  templates: [],
  total: 0, // comes from backend, not always equal to reports.length
  pagination: {
    pageNum: 1,
  },
  isFirstLoad: true,
  isLoaded: false,
  myTemplates: [],
  isLoading: false,
};

export function useReportsHook(stateParam: any): ReportsHook {
  const combinedState = {
    ...initialState,
    ...stateParam,
  };

  const [state, setState] = useState(combinedState);

  const updateReports = (reports: IReport[]) => {
    setState((prev: IState) => ({
      ...prev,
      reports,
    }));
  };

  const updateFirstLoad = (isFirstLoad: boolean) => {
    setState((prev: IState) => ({
      ...prev,
      isFirstLoad,
    }));
  };

  const updateIsLoaded = (isLoaded: boolean) => {
    setState((prev: IState) => ({
      ...prev,
      isLoaded,
    }));
  };

  const updateIsLoading = (isLoading: boolean) => {
    setState((prev: IState) => ({
      ...prev,
      isLoading,
    }));
  };
  const resetReports = () => {
    updateIsLoaded(false);
    setState((prev: IState) => ({
      ...prev,
      reports: [],
    }));
  };

  const appendReports = (reports: IReport[], skipIndex = 0) => {
    setState((prev: IState) => ({
      ...prev,
      reports: [...prev.reports.slice(0, Math.max(0, skipIndex)), ...reports],
    }));
  };

  const setPageNum = (pageNum: number) => {
    setState((prev: IState) => ({
      ...prev,
      pagination: {
        pageNum,
      },
    }));
  };

  const updateTotal = (total: number) => {
    setState((prev: IState) => ({
      ...prev,
      total: Math.max(0, total),
    }));
  };

  const resetReportsState = () => {
    setState((prev: IState) => {
      let isFirstLoad = false;
      if (prev.reports.length === 0) {
        isFirstLoad = true;
      }
      return {
        ...initialState,
        isFirstLoad,
      };
    });
  };

  const updateReportById = (id: string, updatedReport: IReport) => {
    setState((prevState: IState) => {
      const updatedReports = prevState.reports.map(report => {
        if (report.id === id) {
          return updatedReport;
        }
        return report;
      });
      return {
        ...prevState,
        reports: updatedReports,
      };
    });
  };

  const updatePendingReportToFail = (pendingReportIds: number[]) => {
    setState((prevState: IState) => {
      const updatedReports = prevState.reports.map(report => {
        if (pendingReportIds.includes(parseInt(report.id, 10))) {
          return {
            ...report,
            status: 'fail',
          };
        }
        return report;
      });
      return {
        ...prevState,
        reports: updatedReports,
      };
    });
  };

  return {
    state,
    updateReports,
    appendReports,
    setPageNum,
    updateTotal,
    resetReportsState,
    updateReportById,
    updateFirstLoad,
    updateIsLoaded,
    resetReports,
    updateIsLoading,
    updatePendingReportToFail,
  };
}

const ReportsContainer = createContainer(useReportsHook);

export default ReportsContainer;
